/**
 * Eligibility (VOB) modal: retry automated verification or submit manual benefits.
 */

import {
  Alert,
  Anchor,
  Badge,
  Button,
  Group,
  Modal,
  Paper,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
} from '@mantine/core';
import { useForm } from '@mantine/form';
import { zod4Resolver } from 'mantine-form-zod-resolver';
import { useMedplum } from '@medplum/react-hooks';
import { IconAlertTriangle, IconRefresh } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import type { ReactNode } from 'react';

import {
  retryEligibilityVerification,
  submitManualVobEntry,
  toStaffManualVobPayload,
  manualVobServiceDateFromAppointment,
} from '../../lib/eligibilityBotService';
import { ManualVobBenefitFields } from '../../components/billing/ManualVobBenefitFields';
import {
  applyDevManualVobPopulate,
  DevPopulateManualVobButton,
} from '../../lib/billing/devManualVobPopulate';
import {
  defaultManualVobFormValues,
  manualVobSchema,
  type ManualVobFormInput,
} from '../../schemas/manual-vob.schema';
import type { EligibilityRow } from './types';

export interface EligibilityActionModalProps {
  open: boolean;
  onClose: () => void;
  row: EligibilityRow | null;
  onSuccess?: () => void;
}

function formatDate(iso?: string): string | undefined {
  if (!iso) return undefined;
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? iso : d.toLocaleDateString('en-US');
}

function ContextField({ label, value }: { label: string; value?: ReactNode }): JSX.Element {
  return (
    <Stack gap={2}>
      <Text size="xs" c="dimmed" fw={600} tt="uppercase">
        {label}
      </Text>
      <Text size="sm" fw={500}>
        {value ?? '—'}
      </Text>
    </Stack>
  );
}

function SectionHeading({ children }: { children: ReactNode }): JSX.Element {
  return (
    <Text size="xs" c="dimmed" fw={700} tt="uppercase" lts={0.5}>
      {children}
    </Text>
  );
}

export function EligibilityActionModal({
  open,
  onClose,
  row,
  onSuccess,
}: EligibilityActionModalProps): JSX.Element {
  const medplum = useMedplum();
  const [retrying, setRetrying] = useState(false);
  const [retryError, setRetryError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const form = useForm<ManualVobFormInput>({
    initialValues: defaultManualVobFormValues,
    validate: zod4Resolver(manualVobSchema),
  });

  useEffect(() => {
    if (!open || !row) return;
    setRetryError(null);
    setSubmitError(null);
    const billingCode =
      row.serviceTypeCodes?.[0] === 'IOP' || row.serviceTypeCodes?.[0] === 'PCH'
        ? (row.serviceTypeCodes[0] as 'IOP' | 'PCH')
        : 'PSY';
    const serviceDate = manualVobServiceDateFromAppointment(row.soonestBookedAppointment);
    form.setValues({
      ...defaultManualVobFormValues,
      coverageId: row.coverageId ?? '',
      billingCode,
      serviceDate,
    });
  }, [open, row]);

  const handleRetrySohar = async (): Promise<void> => {
    if (!row?.coverageId) {
      setRetryError('No coverage linked to this row.');
      return;
    }
    setRetryError(null);
    setRetrying(true);
    try {
      const result = await retryEligibilityVerification(medplum, row.coverageId);
      if (!result.ok) {
        setRetryError(result.reason ?? 'Verification failed');
        return;
      }
      if (result.skipped && result.reason === 'payer-not-configured') {
        setRetryError('Payer is not configured for automated verification.');
        return;
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setRetryError(err instanceof Error ? err.message : 'Retry failed');
    } finally {
      setRetrying(false);
    }
  };

  const handleSubmit = form.onSubmit(async (values) => {
    setSubmitError(null);
    setSubmitting(true);
    try {
      const result = await submitManualVobEntry(medplum, toStaffManualVobPayload(values));
      if (!result.ok) {
        setSubmitError(result.reason ?? 'Manual entry failed');
        return;
      }
      onSuccess?.();
      onClose();
    } catch (err) {
      setSubmitError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSubmitting(false);
    }
  });

  if (!row) return <></>;

  return (
    <Modal
      opened={open}
      onClose={onClose}
      title={
        <Group justify="space-between" wrap="nowrap">
          <Text fw={700} size="lg">
            Eligibility (VOB)
          </Text>
          <DevPopulateManualVobButton
            onPopulate={() => form.setValues(applyDevManualVobPopulate(form.values))}
          />
        </Group>
      }
      styles={{ title: { flex: 1 } }}
      size="xl"
    >
      <form onSubmit={handleSubmit}>
        <Stack gap="lg">
          {/* Read-only context */}
          <Paper withBorder radius="md" p="lg" bg="var(--mantine-color-gray-0)">
            <SimpleGrid cols={{ base: 2, sm: 4 }} spacing="lg" verticalSpacing="md">
              <ContextField label="Client" value={row.clientName} />
              <ContextField
                label="Phone"
                value={
                  row.clientPhone ? (
                    <Anchor href={`tel:${row.clientPhone}`} size="sm" fw={500}>
                      {row.clientPhone}
                    </Anchor>
                  ) : undefined
                }
              />
              <ContextField label="Payer" value={row.payer} />
              <ContextField label="Member ID" value={row.memberId} />
              <ContextField
                label="Billing service code"
                value={<Badge variant="light" radius="sm">{form.values.billingCode}</Badge>}
              />
              <ContextField label="Submitted" value={formatDate(row.submissionDate)} />
              <ContextField
                label="Next appointment"
                value={
                  row.soonestBookedAppointment
                    ? `${formatDate(row.soonestBookedAppointment)}${
                        row.daysUntilAppointment !== undefined
                          ? ` (${row.daysUntilAppointment}d)`
                          : ''
                      }`
                    : undefined
                }
              />
              <ContextField label="Assumed copay" value={row.assumedCopay} />
            </SimpleGrid>
          </Paper>

          {/* Automated verification (SOHAR) */}
          <Paper withBorder radius="md" p="md">
            <Group justify="space-between" align="center" wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                {row.soharErrorReason ? (
                  <IconAlertTriangle size={20} color="var(--mantine-color-yellow-7)" />
                ) : null}
                <Stack gap={2}>
                  <SectionHeading>Automated verification (SOHAR)</SectionHeading>
                  <Text size="sm" c={row.soharErrorReason ? undefined : 'dimmed'}>
                    {row.soharErrorReason || 'No error on file'}
                  </Text>
                </Stack>
              </Group>
              <Button
                variant="light"
                leftSection={<IconRefresh size={16} />}
                loading={retrying}
                disabled={!row.coverageId}
                onClick={() => void handleRetrySohar()}
                type="button"
              >
                Retry SOHAR
              </Button>
            </Group>
            {retryError && (
              <Alert color="red" variant="light" mt="sm">
                {retryError}
              </Alert>
            )}
          </Paper>

          {/* Manual benefit entry */}
          <Paper withBorder radius="md" p="lg">
            <Stack gap="lg">
              <Group justify="space-between" align="center">
                <Text fw={600}>Benefits (manual entry)</Text>
                <Switch
                  label="Coverage in force / eligible"
                  checked={form.values.inforce}
                  onChange={(e) => form.setFieldValue('inforce', e.currentTarget.checked)}
                />
              </Group>

              <ManualVobBenefitFields form={form} />

              <Stack gap="xs">
                <SectionHeading>Notes</SectionHeading>
                <Textarea
                  placeholder="Disposition or notes about this verification"
                  autosize
                  minRows={2}
                  {...form.getInputProps('disposition')}
                />
              </Stack>
            </Stack>
          </Paper>

          {submitError && (
            <Alert color="red" variant="light">
              {submitError}
            </Alert>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={onClose} type="button">
              Cancel
            </Button>
            <Button type="submit" loading={submitting} disabled={!row.coverageId}>
              Save benefits
            </Button>
          </Group>
        </Stack>
      </form>
    </Modal>
  );
}
